import React, { useState } from "react";
import { generatePath } from "../lib/api";
import { LevelLanguage, Loader, SectionTitle } from "../components/Shared";
import { Check } from "lucide-react";
import { useProfile } from "../context/ProfileContext";

const STARTERS = [
  "Intro to world mythology — heroes & monsters",
  "Optics module — Newton's Disc, Periscope, Kaleidoscope",
  "Career exploration in STEM",
  "Mechanics — motion & forces",
  "Magnetism & electricity basics",
  "Aesop's fables — moral lessons",
];

export default function Paths() {
  const { profile, touch, incr } = useProfile();
  const [topic, setTopic] = useState("");
  const [level, setLevel] = useState(profile.level || "school");
  const [language, setLanguage] = useState(profile.language || "english");
  const [data, setData] = useState(null);
  const [done, setDone] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const go = async (t) => {
    const tt = (t ?? topic).trim();
    if (!tt) return;
    setLoading(true);
    setErr("");
    setData(null);
    setDone({});
    try {
      const res = await generatePath({ topic: tt, level, language });
      setData(res);
      touch();
      incr("pathsStarted");
    } catch (e) {
      setErr("Could not generate learning path. Try another topic.");
    } finally {
      setLoading(false);
    }
  };

  const total = data?.steps?.length || 0;
  const completed = Object.values(done).filter(Boolean).length;
  const pct = total ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-10 py-8 md:py-12" data-testid="paths-page">
      <SectionTitle kicker="Learning paths" title="Your guided journey." subtitle="A short sequence of sessions: explainer → story → questions." />

      <div className="mb-5">
        <LevelLanguage level={level} setLevel={setLevel} language={language} setLanguage={setLanguage} />
      </div>

      <form
        className="grid gap-3 md:grid-cols-[1fr_auto]"
        onSubmit={(e) => { e.preventDefault(); go(); }}
        data-testid="path-form"
      >
        <input
          className="input-field"
          placeholder="What do you want to learn? (e.g. 'optics module')"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          data-testid="path-input"
        />
        <button className="btn-primary" data-testid="path-generate" disabled={loading}>
          Build my path
        </button>
      </form>

      <div className="flex flex-wrap gap-2 mt-4" data-testid="path-starters">
        {STARTERS.map((s, i) => (
          <button
            key={i}
            data-testid={`path-starter-${i}`}
            onClick={() => { setTopic(s); go(s); }}
            className="text-xs bg-white border border-[#DCD7CE] hover:border-[#E07A5F] px-3 py-1.5 rounded-full transition"
          >
            {s}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {loading && <Loader label="Mapping your journey…" />}
        {err && <div className="text-[#D9534F] text-sm" data-testid="path-error">{err}</div>}

        {data && (
          <div className="fadeIn" data-testid="path-output">
            <div className="card p-6 md:p-7 mb-6">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold mb-2">Your path</div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A]">{data.topic}</h2>
              <p className="text-[#4A4A4A] mt-2">{data.overview}</p>
              <div className="mt-5">
                <div className="flex justify-between text-xs text-[#4A4A4A] mb-1">
                  <span>Progress</span><span>{completed}/{total}</span>
                </div>
                <div className="w-full h-2 rounded-full bg-[#EBE7E0]">
                  <div className="h-2 rounded-full bg-[#E07A5F] transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            </div>

            <ol className="grid md:grid-cols-2 gap-5">
              {data.steps.map((s, i) => (
                <li key={i} className="card p-6 flex gap-4" data-testid={`path-step-${i}`}>
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full grid place-items-center font-heading font-bold ${
                    done[i] ? "bg-[#3A5A40] text-white" : "bg-[#FBE7DF] text-[#E07A5F]"
                  }`}>
                    {done[i] ? <Check size={18} /> : s.step}
                  </div>
                  <div className="flex-1">
                    <div className="font-heading text-lg font-semibold text-[#1A1A1A]">{s.title}</div>
                    <div className="text-sm text-[#4A4A4A] mt-1">{s.description}</div>
                    <div className="text-xs text-[#3A5A40] uppercase tracking-[0.18em] font-semibold mt-3">Activity</div>
                    <div className="text-sm text-[#1A1A1A]">{s.activity}</div>
                    <button
                      onClick={() => setDone({ ...done, [i]: !done[i] })}
                      className={`mt-4 text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
                        done[i]
                          ? "bg-[#EAECE6] text-[#3A5A40] border-[#3A5A40]"
                          : "bg-white border-[#DCD7CE] text-[#1A1A1A] hover:border-[#3A5A40]"
                      }`}
                      data-testid={`path-toggle-${i}`}
                    >
                      {done[i] ? "Completed" : "Mark complete"}
                    </button>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}
